import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import Routes from '../data/routes'
import calculateActiveCheckoutStep from '../lib/calculate-active-checkout-step'
import calculateCheckoutSteps from '../lib/calculate-checkout-steps'
import { BlockingError } from '../lib/custom-errors'
import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import { useIntl } from '../localization/react'
import { OrderState } from '../typings/Order'
import { PaymentState } from '../typings/Payment'

import {
  InitialDataLoaderCartDocument,
  InitialDataLoaderCartQuery,
  InitialDataLoaderOrderDocument,
  InitialDataLoaderOrderQuery,
  Origin,
} from '../graphql'
import CheckoutContext from './CheckoutContext'

import LoadingPage from './LoadingPage'
import QuickCheckoutHandler from './QuickCheckoutHandler'
import sessionUtils from './session-utils'
import useCart from './useCart'
import useOrder from './useOrder'
import useVenueEnabledFeaturePreviews, { VenueFeaturePreviews } from './useVenueEnabledFeaturePreviews'

const InitialDataHandler: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const location = useLocation()
  const history = useHistory()
  const intl = useIntl()

  const { cartId, orderId, skipConfirmation, venueId, origin } = getPhoenixUrlParams(location.search)
  const [initialSteps, setInitialSteps] = useState<Array<string>>([])
  const [blockingError, setBlockingError] = useState<{ name: string; message: string } | undefined>()

  const { cartData, isCartLoading, refetchCart } = useCart<InitialDataLoaderCartQuery>(InitialDataLoaderCartDocument, {
    skip: !cartId || initialSteps.length > 0,
  })

  const { orderData, isOrderLoading } = useOrder<InitialDataLoaderOrderQuery>(InitialDataLoaderOrderDocument, {
    skip: !orderId || initialSteps.length > 0,
  })

  const { enabledFeatures, loading: isLoadingVenueEnabledFeatures } = useVenueEnabledFeaturePreviews({ venueId })

  // No Eversports Branding => Venue is part of feature switch, AND is origin=widget
  // Later this will come from the backend - we will set up an extension, and see if the venue has this extension enabled
  const isVenueBrandingEnabled =
    origin === Origin.ORIGIN_WIDGET && enabledFeatures.includes(VenueFeaturePreviews.HIDE_EVERSPORTS_BRANDING)

  useEffect(() => {
    if (location.pathname !== Routes.CONFIRMATION && sessionUtils.getConfirmationWasSuccessful()) {
      setBlockingError({ name: 'The order was already confirmed', message: intl.orderAlreadyConfirmed() })
    }
  }, [location.pathname])

  useEffect(() => {
    if (!blockingError) return
    throw new BlockingError(blockingError.name, blockingError.message)
  }, [blockingError])

  if (initialSteps.length > 0 && !isLoadingVenueEnabledFeatures) {
    return (
      <CheckoutContext
        initialState={{
          initialSteps,
          isVenueBrandingEnabled,
        }}
        refetchCart={refetchCart}
      >
        {children}
      </CheckoutContext>
    )
  }

  if (!isCartLoading && cartData && initialSteps.length === 0 && !blockingError) {
    const cartInitialSteps = calculateCheckoutSteps(cartData.cart)
    const cartInitialActiveStep = calculateActiveCheckoutStep(cartData.cart)

    if (cartInitialActiveStep.type === 'error') {
      const error = cartInitialActiveStep.error

      if (error === 'no-available-online-product') {
        setBlockingError({
          name: 'No available product for online purchase',
          message: intl.noAvailableProductForOnlinePurchase(),
        })
        return null
      }

      throw new Error(error)
    }

    if (location.pathname === '/') {
      history.replace(`${cartInitialActiveStep.route}?cartId=${cartId}`)
      return null
    }

    setInitialSteps(cartInitialSteps)
  }

  if (!isOrderLoading && orderData && initialSteps.length === 0 && !blockingError) {
    // An order can have been aborted before we enter the checkout => error page
    if (orderData.order.state === OrderState.ORDER_STATE_ABORTED) {
      setBlockingError({ name: 'The order was aborted', message: intl.orderAbortedError() })
      return null
    }

    if (orderData.order.payment && orderData.order.payment.state === PaymentState.PAYMENT_STATE_CANCELLED) {
      setBlockingError({ name: 'The payment was cancelled', message: intl.paymentErrorCancelled() })
      return null
    }

    const orderInitialSteps = calculateCheckoutSteps(orderData.order)
    const orderInitialActiveStep = calculateActiveCheckoutStep(orderData.order)

    if (orderInitialActiveStep.type === 'error') {
      throw new Error(orderInitialActiveStep.error)
    }

    if (location.pathname === '/') {
      if (orderInitialActiveStep.route === Routes.CONFIRMATION && skipConfirmation) {
        return (
          <CheckoutContext
            initialState={{
              initialSteps,
              isVenueBrandingEnabled,
            }}
            refetchCart={refetchCart}
          >
            <QuickCheckoutHandler />
          </CheckoutContext>
        )
      }

      history.replace(`${orderInitialActiveStep.route}?orderId=${orderId}`)
      return null
    }

    // If somehow we get to the payment page with an order that should already be on the confirmation page
    // we redirect it to the confirmation page
    if (location.pathname === Routes.PAYMENT && orderInitialActiveStep.route === Routes.CONFIRMATION) {
      history.replace(`${Routes.CONFIRMATION}?orderId=${orderId}`)
      return null
    }

    setInitialSteps(orderInitialSteps)
  }

  return <LoadingPage />
}

export default InitialDataHandler
